'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { appFetch } from '@/data/api/app-fetch'
import { localDb } from '@/data/local/database'
import { cancelFocusDoneNotification, scheduleFocusDoneNotification } from '@/lib/notifications'
import { playSound } from '@/lib/sounds'
import {
  BUILT_IN_MIXES,
  DEFAULT_MIX,
  mixSignature,
  normalizeTracks,
  type SoundscapeId,
  type SoundscapeMix,
  type SoundscapeTrackState,
} from './catalog'
import { SoundscapeEngine } from './engine'

const ACTIVE_TIMER_KEY = 'activeFocusTimer'
const PREFERENCES_KEY = 'shannian.focus-soundscape.v2'

interface PersistedPreferences {
  duration: number
  timeLeft: number
  focusModule: string
  subject: string
  tracks: SoundscapeTrackState[]
  savedMixes: SoundscapeMix[]
  currentMixName: string
  dockCollapsed: boolean
}

interface ActiveTimerRecord {
  deadlineMs: number
  duration: number
  focusModule?: string
  subject?: string
}

interface FocusSoundscapeContextValue {
  duration: number
  timeLeft: number
  isRunning: boolean
  focusModule: string
  subject: string
  completedSubject: string | null
  sessionRevision: number
  selectedTracks: SoundscapeTrackState[]
  savedMixes: SoundscapeMix[]
  currentMixName: string
  isSoundscapePlaying: boolean
  audioMessage: string
  dockCollapsed: boolean
  isStudioOpen: boolean
  setFocusModule: (value: string) => void
  setSubject: (value: string) => void
  selectDuration: (minutes: number) => void
  adjustDuration: (delta: number) => void
  toggleTimer: () => void
  resetTimer: () => void
  endAndRecord: () => Promise<void>
  playSoundscape: () => Promise<void>
  pauseSoundscape: () => void
  toggleSoundscape: () => void
  nextMix: () => void
  applyMix: (mix: SoundscapeMix) => void
  toggleTrack: (id: SoundscapeId) => void
  setTrackVolume: (id: SoundscapeId, volume: number) => void
  toggleFavorite: () => void
  removeSavedMix: (id: string) => void
  setDockCollapsed: (value: boolean) => void
  setStudioOpen: (value: boolean) => void
}

const FocusSoundscapeContext = createContext<FocusSoundscapeContextValue | null>(null)

function loadPreferences(): PersistedPreferences {
  const fallback: PersistedPreferences = {
    duration: 25,
    timeLeft: 25 * 60,
    focusModule: '资料分析',
    subject: '',
    tracks: DEFAULT_MIX.tracks,
    savedMixes: [],
    currentMixName: DEFAULT_MIX.name,
    dockCollapsed: false,
  }
  if (typeof window === 'undefined') return fallback
  try {
    const parsed = JSON.parse(window.localStorage.getItem(PREFERENCES_KEY) || '{}') as Partial<PersistedPreferences>
    const duration = Math.max(1, Math.min(1440, Math.round(Number(parsed.duration) || fallback.duration)))
    return {
      duration,
      timeLeft: Math.max(0, Math.min(duration * 60, Math.round(Number(parsed.timeLeft) || duration * 60))),
      focusModule: typeof parsed.focusModule === 'string' ? parsed.focusModule : fallback.focusModule,
      subject: typeof parsed.subject === 'string' ? parsed.subject : '',
      tracks: normalizeTracks(Array.isArray(parsed.tracks) ? parsed.tracks : fallback.tracks),
      savedMixes: Array.isArray(parsed.savedMixes)
        ? parsed.savedMixes
            .filter(mix => mix && typeof mix.id === 'string' && typeof mix.name === 'string')
            .map(mix => ({ ...mix, tracks: normalizeTracks(mix.tracks || []) }))
            .filter(mix => mix.tracks.length > 0)
        : [],
      currentMixName: typeof parsed.currentMixName === 'string' ? parsed.currentMixName : fallback.currentMixName,
      dockCollapsed: parsed.dockCollapsed === true,
    }
  } catch {
    return fallback
  }
}

export function FocusSoundscapeProvider({ children }: { children: ReactNode }) {
  const initial = useMemo(() => loadPreferences(), [])
  const [duration, setDuration] = useState(initial.duration)
  const [timeLeft, setTimeLeft] = useState(initial.timeLeft)
  const [isRunning, setIsRunning] = useState(false)
  const [focusModule, setFocusModule] = useState(initial.focusModule)
  const [subject, setSubject] = useState(initial.subject)
  const [completedSubject, setCompletedSubject] = useState<string | null>(null)
  const [sessionRevision, setSessionRevision] = useState(0)
  const [selectedTracks, setSelectedTracks] = useState(initial.tracks.length ? initial.tracks : DEFAULT_MIX.tracks)
  const [savedMixes, setSavedMixes] = useState(initial.savedMixes)
  const [currentMixName, setCurrentMixName] = useState(initial.currentMixName)
  const [isSoundscapePlaying, setIsSoundscapePlaying] = useState(false)
  const [audioMessage, setAudioMessage] = useState('')
  const [dockCollapsed, setDockCollapsed] = useState(initial.dockCollapsed)
  const [isStudioOpen, setStudioOpen] = useState(false)
  const deadlineRef = useRef<number | null>(null)
  const completionInFlightRef = useRef(false)
  const engineRef = useRef<SoundscapeEngine | null>(null)
  const stateRef = useRef({ duration, timeLeft, focusModule, subject, selectedTracks, isSoundscapePlaying })

  useEffect(() => {
    stateRef.current = { duration, timeLeft, focusModule, subject, selectedTracks, isSoundscapePlaying }
  }, [duration, focusModule, isSoundscapePlaying, selectedTracks, subject, timeLeft])

  useEffect(() => {
    const preferences: PersistedPreferences = {
      duration,
      timeLeft,
      focusModule,
      subject,
      tracks: selectedTracks,
      savedMixes,
      currentMixName,
      dockCollapsed,
    }
    try { window.localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences)) } catch { /* private mode */ }
  }, [currentMixName, dockCollapsed, duration, focusModule, savedMixes, selectedTracks, subject, timeLeft])

  const saveSession = useCallback(async (
    minutes: number,
    reachedTarget: boolean,
    meta: { focusModule: string; subject: string },
  ) => {
    await appFetch('/api/focus', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        minutes,
        reachedTarget,
        focusModule: meta.focusModule,
        subject: meta.subject.trim(),
        theme: 'soundscape',
      }),
    })
    setSessionRevision(value => value + 1)
  }, [])

  const pauseSoundscape = useCallback(() => {
    engineRef.current?.pause()
    setIsSoundscapePlaying(false)
  }, [])

  const playSoundscape = useCallback(async () => {
    const tracks = stateRef.current.selectedTracks.length ? stateRef.current.selectedTracks : DEFAULT_MIX.tracks
    try {
      if (!engineRef.current) engineRef.current = new SoundscapeEngine()
      await engineRef.current.play(tracks)
      setIsSoundscapePlaying(true)
      setAudioMessage('')
    } catch (error) {
      setIsSoundscapePlaying(false)
      setAudioMessage(error instanceof Error ? error.message : '声景暂时无法播放')
    }
  }, [])

  useEffect(() => {
    let disposed = false
    void localDb.transaction('rw', localDb.appMetadata, async () => {
      const record = await localDb.appMetadata.get(ACTIVE_TIMER_KEY)
      const value = record?.value as ActiveTimerRecord | undefined
      if (!value?.deadlineMs || !value.duration) return null
      if (value.deadlineMs <= Date.now()) await localDb.appMetadata.delete(ACTIVE_TIMER_KEY)
      return value
    }).then(value => {
      if (disposed || !value) return
      const remaining = Math.max(0, Math.ceil((value.deadlineMs - Date.now()) / 1000))
      setDuration(value.duration)
      setFocusModule(value.focusModule || '资料分析')
      setSubject(value.subject || '')
      if (remaining > 0) {
        deadlineRef.current = value.deadlineMs
        setTimeLeft(remaining)
        setIsRunning(true)
      } else {
        const finishedName = (value.subject || '').trim() || value.focusModule || '自主专注'
        setTimeLeft(0)
        setCompletedSubject(finishedName)
        void saveSession(value.duration, true, {
          focusModule: value.focusModule || '资料分析',
          subject: value.subject || '',
        })
      }
    })
    return () => { disposed = true }
  }, [saveSession])

  useEffect(() => {
    if (!isRunning) return
    const update = () => {
      const remaining = Math.max(0, Math.ceil(((deadlineRef.current || Date.now()) - Date.now()) / 1000))
      if (remaining > 0) {
        setTimeLeft(remaining)
        return
      }
      if (completionInFlightRef.current) return
      completionInFlightRef.current = true
      const current = stateRef.current
      setTimeLeft(0)
      setIsRunning(false)
      deadlineRef.current = null
      setCompletedSubject(current.subject.trim() || current.focusModule || '自主专注')
      void localDb.appMetadata.delete(ACTIVE_TIMER_KEY)
      void cancelFocusDoneNotification()
      pauseSoundscape()
      playSound('success')
      void saveSession(current.duration, true, { focusModule: current.focusModule, subject: current.subject })
        .finally(() => { completionInFlightRef.current = false })
    }
    update()
    const timer = window.setInterval(update, 1000)
    return () => window.clearInterval(timer)
  }, [isRunning, pauseSoundscape, saveSession])

  useEffect(() => {
    if (!isRunning || !deadlineRef.current) return
    void localDb.appMetadata.put({
      key: ACTIVE_TIMER_KEY,
      value: { deadlineMs: deadlineRef.current, duration, focusModule, subject },
      updatedAtMs: Date.now(),
    })
  }, [duration, focusModule, isRunning, subject])

  useEffect(() => () => engineRef.current?.destroy(), [])

  const selectDuration = useCallback((minutes: number) => {
    const safeMinutes = Math.max(1, Math.min(1440, Math.round(minutes) || 1))
    setIsRunning(false)
    setDuration(safeMinutes)
    setTimeLeft(safeMinutes * 60)
    setCompletedSubject(null)
    deadlineRef.current = null
    void localDb.appMetadata.delete(ACTIVE_TIMER_KEY)
    void cancelFocusDoneNotification()
    pauseSoundscape()
  }, [pauseSoundscape])

  const adjustDuration = useCallback((delta: number) => {
    selectDuration(stateRef.current.duration + delta)
  }, [selectDuration])

  const toggleTimer = useCallback(() => {
    const current = stateRef.current
    if (isRunning) {
      const remaining = Math.max(0, Math.ceil(((deadlineRef.current || Date.now()) - Date.now()) / 1000))
      setTimeLeft(remaining)
      setIsRunning(false)
      deadlineRef.current = null
      void localDb.appMetadata.delete(ACTIVE_TIMER_KEY)
      void cancelFocusDoneNotification()
      pauseSoundscape()
      playSound('close')
      return
    }
    const seconds = current.timeLeft === 0 ? current.duration * 60 : current.timeLeft
    const deadlineMs = Date.now() + seconds * 1000
    deadlineRef.current = deadlineMs
    setTimeLeft(seconds)
    setIsRunning(true)
    setCompletedSubject(null)
    playSound('open')
    void playSoundscape()
    void scheduleFocusDoneNotification(current.subject.trim() || current.focusModule, deadlineMs)
    void localDb.appMetadata.put({
      key: ACTIVE_TIMER_KEY,
      value: { deadlineMs, duration: current.duration, focusModule: current.focusModule, subject: current.subject },
      updatedAtMs: Date.now(),
    })
  }, [isRunning, pauseSoundscape, playSoundscape])

  const resetTimer = useCallback(() => {
    const current = stateRef.current
    setIsRunning(false)
    deadlineRef.current = null
    void localDb.appMetadata.delete(ACTIVE_TIMER_KEY)
    void cancelFocusDoneNotification()
    setCompletedSubject(null)
    setTimeLeft(current.duration * 60)
    pauseSoundscape()
  }, [pauseSoundscape])

  const endAndRecord = useCallback(async () => {
    const current = stateRef.current
    const elapsedSeconds = current.duration * 60 - current.timeLeft
    setIsRunning(false)
    deadlineRef.current = null
    await localDb.appMetadata.delete(ACTIVE_TIMER_KEY)
    void cancelFocusDoneNotification()
    pauseSoundscape()
    playSound('close')
    setTimeLeft(current.duration * 60)
    if (elapsedSeconds >= 30) {
      await saveSession(Math.max(1, Math.round(elapsedSeconds / 60)), false, {
        focusModule: current.focusModule,
        subject: current.subject,
      })
    }
  }, [pauseSoundscape, saveSession])

  const applyMix = useCallback((mix: SoundscapeMix) => {
    const tracks = normalizeTracks(mix.tracks)
    if (!tracks.length) return
    setSelectedTracks(tracks)
    stateRef.current.selectedTracks = tracks
    setCurrentMixName(mix.name)
    engineRef.current?.setTracks(tracks)
    playSound('snap')
  }, [])

  const nextMix = useCallback(() => {
    const playlist = savedMixes.length ? [...savedMixes, ...BUILT_IN_MIXES] : BUILT_IN_MIXES
    const currentSignature = mixSignature(stateRef.current.selectedTracks)
    const currentIndex = playlist.findIndex(mix => mixSignature(mix.tracks) === currentSignature)
    const next = playlist[(currentIndex + 1 + playlist.length) % playlist.length]
    applyMix(next)
  }, [applyMix, savedMixes])

  const toggleTrack = useCallback((id: SoundscapeId) => {
    setSelectedTracks(current => {
      const exists = current.some(track => track.id === id)
      if (exists) {
        const next = current.filter(track => track.id !== id)
        if (!next.length) {
          setAudioMessage('至少保留一种声景')
          return current
        }
        setAudioMessage('')
        stateRef.current.selectedTracks = next
        engineRef.current?.setTracks(next)
        setCurrentMixName('自定义声景')
        return next
      }
      if (current.length >= 3) {
        setAudioMessage('最多同时混合三种声景')
        return current
      }
      const next = [...current, { id, volume: 0.52 }]
      setAudioMessage('')
      stateRef.current.selectedTracks = next
      engineRef.current?.setTracks(next)
      setCurrentMixName('自定义声景')
      return next
    })
    playSound('pop')
  }, [])

  const setTrackVolume = useCallback((id: SoundscapeId, volume: number) => {
    const safeVolume = Math.max(0, Math.min(1, volume))
    setSelectedTracks(current => {
      const next = current.map(track => track.id === id ? { ...track, volume: safeVolume } : track)
      stateRef.current.selectedTracks = next
      return next
    })
    engineRef.current?.setVolume(id, safeVolume)
    setCurrentMixName('自定义声景')
  }, [])

  const toggleFavorite = useCallback(() => {
    const signature = mixSignature(stateRef.current.selectedTracks)
    setSavedMixes(current => {
      const existing = current.find(mix => mixSignature(mix.tracks) === signature)
      if (existing) {
        setAudioMessage('已从收藏中移除')
        return current.filter(mix => mix.id !== existing.id)
      }
      const customCount = current.filter(mix => mix.name.startsWith('我的声景')).length + 1
      const mix: SoundscapeMix = {
        id: `custom-${Date.now()}`,
        name: `我的声景 ${customCount}`,
        tracks: stateRef.current.selectedTracks,
        createdAt: Date.now(),
      }
      setCurrentMixName(mix.name)
      setAudioMessage('组合已收藏')
      return [mix, ...current]
    })
    playSound('check')
  }, [])

  const removeSavedMix = useCallback((id: string) => {
    setSavedMixes(current => current.filter(mix => mix.id !== id))
    setAudioMessage('已删除收藏组合')
    playSound('drop')
  }, [])

  const value = useMemo<FocusSoundscapeContextValue>(() => ({
    duration,
    timeLeft,
    isRunning,
    focusModule,
    subject,
    completedSubject,
    sessionRevision,
    selectedTracks,
    savedMixes,
    currentMixName,
    isSoundscapePlaying,
    audioMessage,
    dockCollapsed,
    isStudioOpen,
    setFocusModule,
    setSubject,
    selectDuration,
    adjustDuration,
    toggleTimer,
    resetTimer,
    endAndRecord,
    playSoundscape,
    pauseSoundscape,
    toggleSoundscape: () => { if (isSoundscapePlaying) pauseSoundscape(); else void playSoundscape() },
    nextMix,
    applyMix,
    toggleTrack,
    setTrackVolume,
    toggleFavorite,
    removeSavedMix,
    setDockCollapsed,
    setStudioOpen,
  }), [
    adjustDuration, applyMix, audioMessage, completedSubject, currentMixName, dockCollapsed, duration,
    endAndRecord, focusModule, isRunning, isSoundscapePlaying, isStudioOpen, nextMix, pauseSoundscape,
    playSoundscape, removeSavedMix, resetTimer, savedMixes, selectDuration, selectedTracks, sessionRevision,
    setTrackVolume, subject, timeLeft, toggleFavorite, toggleTimer, toggleTrack,
  ])

  return <FocusSoundscapeContext.Provider value={value}>{children}</FocusSoundscapeContext.Provider>
}

export function useFocusSoundscape() {
  const value = useContext(FocusSoundscapeContext)
  if (!value) throw new Error('useFocusSoundscape must be used inside FocusSoundscapeProvider')
  return value
}
